import axios, { AxiosInstance, AxiosError, type AxiosRequestHeaders } from 'axios';
import * as Sentry from "@sentry/react";
import { platformService } from '@/services/platform';
import { userStorage } from '@/utils/userStorage';
import { isDeviceOnline } from '@/services/network';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Custom API error that preserves HTTP status code for proper error handling
 */
export class ApiError extends Error {
    public readonly statusCode: number | undefined;
    public readonly isNetworkError: boolean;
    public readonly errorCode: string | undefined;
    public readonly responseData: unknown;
    public readonly url: string | undefined;
    public readonly method: string | undefined;
    /** Flag to prevent duplicate Sentry captures - set to true after first capture */
    public sentryReported: boolean = false;
    /**
     * True when this is a network failure AND the device reported no internet
     * connectivity at the moment it was thrown. These are expected (the user is
     * offline) and must never be reported to Sentry — see the network policy in
     * the response interceptor below and `beforeSend` in instrument.js.
     */
    public isOffline: boolean = false;

    constructor(
        message: string,
        statusCode?: number,
        isNetworkError = false,
        errorCode?: string,
        responseData?: unknown,
        url?: string,
        method?: string
    ) {
        super(message);
        this.name = 'ApiError';
        this.statusCode = statusCode;
        this.isNetworkError = isNetworkError;
        this.errorCode = errorCode;
        this.responseData = responseData;
        this.url = url;
        this.method = method;
        // Maintain proper prototype chain for instanceof checks
        Object.setPrototypeOf(this, ApiError.prototype);
    }

    get isUnauthorized(): boolean {
        return this.statusCode === 401;
    }

    get isForbidden(): boolean {
        return this.statusCode === 403;
    }

    /**
     * GreekBrain paid-feature gating: subscription required (402) or monthly
     * quota exceeded (429 QUOTA_EXCEEDED). These are handled gracefully by the
     * paywall / limit toast and must NOT be reported to Sentry as errors.
     */
    get isEntitlementError(): boolean {
        return (this.statusCode === 402 && this.errorCode === 'SUBSCRIPTION_REQUIRED')
            || (this.statusCode === 429 && this.errorCode === 'QUOTA_EXCEEDED');
    }

    get isServerError(): boolean {
        return this.statusCode !== undefined && this.statusCode >= 500;
    }

    get isClientError(): boolean {
        return this.statusCode !== undefined && this.statusCode >= 400 && this.statusCode < 500;
    }

    /** Returns true if this error should be retried (network errors, 5xx, 429) */
    get isRetryable(): boolean {
        return this.isNetworkError || this.isServerError || this.statusCode === 429;
    }
}

export class ApiClient {
    protected client: AxiosInstance;
    private token: string | null = null;

    private initPromise: Promise<void>;

    // Global 401 auth handler: shows native login and retries on success
    private authHandler: (() => Promise<boolean>) | null = null;
    private authHandlerPromise: Promise<boolean> | null = null;

    // Global GreekBrain entitlement handler: invoked when a paid AI feature is
    // blocked. 'subscription' (HTTP 402) → open the subscription paywall;
    // 'quota' (HTTP 429 QUOTA_EXCEEDED) → show a "monthly limit reached" notice.
    private entitlementHandler: ((kind: 'subscription' | 'quota', message: string) => void) | null = null;

    constructor() {
        this.client = axios.create({
            baseURL: API_BASE_URL,
            withCredentials: true, // Keep for session fallback
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            timeout: 30000,
        });

        // Load token from storage on initialization
        this.initPromise = this.loadToken();

        // Add request interceptor to attach token and platform info
        this.client.interceptors.request.use(
            async (config) => {
                // Ensure token is loaded before request
                await this.initPromise;

                if (this.token) {
                    config.headers.Authorization = `Bearer ${this.token}`;
                }

                // Add platform detection headers
                this.addPlatformHeaders(config.headers);

                return config;
            },
            (error) => Promise.reject(error)
        );

        // Handle errors globally with user-friendly messages
        this.client.interceptors.response.use(
            (response) => response,
            async (error: AxiosError<{ message?: string; error?: string; error_code?: string; success?: boolean }>) => {
                let userMessage = 'An unexpected error occurred. Please try again.';
                const statusCode: number | undefined = error.response?.status;
                let isNetworkError = false;
                // Prefer the structured error_code (V2 ApiResponse / writing controller);
                // fall back to the legacy `error` string for older endpoints.
                const errorCode = error.response?.data?.error_code ?? error.response?.data?.error;
                const responseData = error.response?.data;
                let shouldTryAuthHandler = false;

                if (error.response) {
                    // Extract backend message if available
                    const backendMessage = error.response.data?.message;

                    switch (error.response.status) {
                        case 401: {
                            // Check if the token has changed since the request was made
                            // This prevents an old request (with an expired token) from logging out a user
                            // who has just logged in with a new valid token.
                            const requestTokenHeader = error.config?.headers?.Authorization;
                            const currentTokenHeader = this.token ? `Bearer ${this.token}` : undefined;

                            // If we have a current token, and the request used a different token (or no token),
                            // then ignore this 401 for the purpose of clearing the session.
                            if (this.token && requestTokenHeader !== currentTokenHeader) {
                                console.warn('Ignoring 401 from old/mismatched token. Current session preserved.');
                                // Still reject the promise so the caller knows it failed
                                throw new ApiError('Request failed due to token mismatch', 401, false, errorCode, responseData);
                            }

                            // No token was sent — unauthenticated request (guest mode)
                            if (!requestTokenHeader) {
                                userMessage = backendMessage || 'Authentication required.';
                                // Don't trigger auth handler for session migration attempts —
                                // the caller handles the failure and enters guest mode
                                const isAuthMigration = error.config?.url?.includes('/api/auth/token');
                                shouldTryAuthHandler = !isAuthMigration;
                                break;
                            }

                            // Token was sent but rejected — genuinely expired/invalid
                            userMessage = backendMessage || 'Your session has expired. Please log in again.';
                            shouldTryAuthHandler = true;
                            Sentry.addBreadcrumb({
                                category: 'auth',
                                message: 'Token cleared due to 401',
                                level: 'warning',
                                data: {
                                    url: error.config?.url,
                                    method: error.config?.method?.toUpperCase(),
                                    had_token: true,
                                    backend_message: backendMessage,
                                    platform: platformService.detectPlatform(),
                                    is_telegram: platformService.isTelegramMiniApp(),
                                },
                            });
                            this.clearToken();
                            break;
                        }
                        case 402:
                            userMessage = backendMessage || error.response.data?.error || 'Subscription required.';
                            break;
                        case 403:
                            userMessage = backendMessage || 'You do not have permission to access this resource.';
                            break;
                        case 404:
                            userMessage = backendMessage || 'The requested resource was not found.';
                            break;
                        case 422:
                            userMessage = backendMessage || 'Invalid data provided.';
                            break;
                        case 429:
                            userMessage = backendMessage || 'Too many requests. Please slow down and try again.';
                            break;
                        case 500:
                        case 502:
                        case 503:
                            userMessage = backendMessage || 'Server error. Our team has been notified. Please try again later.';
                            break;
                        default:
                            userMessage = backendMessage ||
                                error.response.data?.error ||
                                'Something went wrong. Please try again.';
                    }
                } else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
                    // Axios marks timeouts with ECONNABORTED (and sets error.request),
                    // so this must be checked before the generic no-response branch.
                    userMessage = 'Request timed out. Please try again.';
                    isNetworkError = true;
                } else if (error.request) {
                    isNetworkError = true;
                    // Differentiate "no internet on the device" from "device is
                    // online but our server is unreachable" for a clearer message.
                    userMessage = isDeviceOnline()
                        ? 'We’re having trouble reaching the server. Please try again.'
                        : 'Unable to connect to the server. Please check your internet connection.';
                }

                const url = error.config?.url;
                const method = error.config?.method?.toUpperCase();

                console.error('API Error:', {
                    message: error.message,
                    status: error.response?.status,
                    url,
                    data: error.response?.data
                });

                const apiError = new ApiError(
                    userMessage,
                    statusCode,
                    isNetworkError,
                    errorCode,
                    responseData,
                    url,
                    method
                );

                // A network failure while the device itself reports no internet
                // is expected (the user is offline) — flag it so it is never sent
                // to Sentry, by any handler.
                const deviceOffline = isNetworkError && !isDeviceOnline();
                apiError.isOffline = deviceOffline;

                // Sentry reporting policy. The event itself (grouping, tags,
                // extra, title) is shaped centrally in `beforeSend` (instrument.js);
                // here we only decide WHETHER to report and prevent duplicates.
                //
                // Report — "error connecting to the server", grouped as ONE issue:
                //   - any 5xx server error
                //   - a network failure while the device is online (server
                //     unreachable / timed out / DNS), i.e. not the user being offline
                // Skip here (handled elsewhere or intentionally not reported):
                //   - offline network failures (user has no internet)
                //   - 401 unauthorized (handled in the auth flow)
                //   - entitlement / other 4xx client errors
                const isServerConnectionError = apiError.isServerError || (isNetworkError && !deviceOffline);

                if (deviceOffline) {
                    // Belt-and-suspenders: make sure no other handler reports it.
                    apiError.sentryReported = true;
                } else if (isServerConnectionError) {
                    // captureException runs `beforeSend` synchronously, which sets
                    // the dedupe marker; flag sentryReported afterwards so the
                    // global query/mutation handlers don't capture it again.
                    Sentry.captureException(apiError);
                    apiError.sentryReported = true;
                }

                // GreekBrain paid-feature gating: surface the paywall / limit notice
                // globally so individual call sites don't each need to handle it.
                // A request can opt out (skipEntitlementHandler) when it shows its
                // own friendly upsell instead — e.g. the voice-dialogue session.
                const skipEntitlement = (error.config as { skipEntitlementHandler?: boolean } | undefined)?.skipEntitlementHandler === true;
                if (this.entitlementHandler && !skipEntitlement) {
                    const entitlementMessage = error.response?.data?.message
                        || error.response?.data?.error
                        || userMessage;
                    if (statusCode === 402 && errorCode === 'SUBSCRIPTION_REQUIRED') {
                        this.entitlementHandler('subscription', entitlementMessage);
                    } else if (statusCode === 429 && errorCode === 'QUOTA_EXCEEDED') {
                        this.entitlementHandler('quota', entitlementMessage);
                    }
                }

                // For 401s: try showing native login and retry the request
                if (shouldTryAuthHandler && this.authHandler && error.config) {
                    try {
                        // Deduplicate: if login sheet is already showing, wait for it
                        if (!this.authHandlerPromise) {
                            this.authHandlerPromise = this.authHandler().finally(() => {
                                this.authHandlerPromise = null;
                            });
                        }
                        const success = await this.authHandlerPromise;
                        if (success && this.token) {
                            // Retry the original request with the new token
                            error.config.headers.Authorization = `Bearer ${this.token}`;
                            return this.client.request(error.config);
                        }
                    } catch {
                        // Login dismissed or failed — fall through to reject
                    }
                }

                throw apiError;
            }
        );
    }

    /**
     * Ensure the client is initialized (token loaded)
     */
    async ensureInitialized(): Promise<void> {
        await this.initPromise;
    }

    /**
     * Register a global auth handler for 401 responses.
     * When a 401 occurs, this handler is called (e.g. to show native login).
     * If it resolves with true (user authenticated), the failed request is retried.
     */
    setAuthHandler(handler: () => Promise<boolean>): void {
        this.authHandler = handler;
    }

    /**
     * Register a global handler for GreekBrain paid-feature gating responses.
     * Called with 'subscription' on HTTP 402 (open the paywall) and 'quota' on
     * HTTP 429 QUOTA_EXCEEDED (show a monthly-limit notice).
     */
    setEntitlementHandler(handler: (kind: 'subscription' | 'quota', message: string) => void): void {
        this.entitlementHandler = handler;
    }

    /**
     * Add platform detection headers to requests
     */
    private addPlatformHeaders(headers: AxiosRequestHeaders): void {
        // Detect if running in Capacitor (native app)
        if (platformService.isNativeApp()) {
            const platform = platformService.detectPlatform();
            headers['X-Capacitor-Platform'] = platform === 'ios' ? 'ios' :
                platform === 'android' ? 'android' : 'web';
        }

        // Detect if running in Telegram Mini App
        if (platformService.isTelegramMiniApp()) {
            headers['X-Telegram-Web-App'] = 'true';
        }

        // Which of the two apps this request belongs to. The backend serves both
        // from api.hallouminati.app, so the host tells it nothing and only a few
        // endpoints carry an `app_type` parameter — this header is the one signal
        // present on every call (it routes errors to the right Sentry project).
        const appType = platformService.getResolvedAppType();
        if (appType) {
            headers['X-App-Type'] = appType;
        }
    }

    /**
     * Load (or reload) token from storage
     */
    async loadToken(force = false): Promise<void> {
        try {
            let loadedToken: string | null = null;

            if (platformService.isNativeApp()) {
                // Use secure storage for native apps
                const { Preferences } = await import('@capacitor/preferences');
                const { value } = await Preferences.get({ key: 'auth_token' });
                loadedToken = value;
            } else {
                // Use user-namespaced storage for web/Telegram
                loadedToken = userStorage.getItem('auth_token');
            }

            // On initial load: only set if not already set (e.g. by login happening during load)
            // On force reload (after native login): always update
            if (force || this.token === null) {
                this.token = loadedToken;
            }
        } catch (error) {
            console.error('Failed to load token:', error);
            Sentry.captureException(error);
            // Don't overwrite if error
            if (this.token === null) {
                this.token = null;
            }
        }
    }

    /**
     * Save token to storage
     */
    async saveToken(token: string): Promise<void> {
        this.token = token;

        try {
            if (platformService.isNativeApp()) {
                const { Preferences } = await import('@capacitor/preferences');
                await Preferences.set({ key: 'auth_token', value: token });
            } else {
                userStorage.setItem('auth_token', token);
            }
            userStorage.setItem('isAuthenticated', 'true');
        } catch (error) {
            console.error('Failed to save token:', error);
            Sentry.captureException(error);
        }
    }

    /**
     * Clear token from storage
     */
    async clearToken(): Promise<void> {
        this.token = null;

        try {
            if (platformService.isNativeApp()) {
                const { Preferences } = await import('@capacitor/preferences');
                await Preferences.remove({ key: 'auth_token' });
            } else {
                userStorage.removeItem('auth_token');
            }
            userStorage.removeItem('isAuthenticated');
        } catch (error) {
            console.error('Failed to clear token:', error);
            Sentry.captureException(error);
        }
    }

    /**
     * Get current token
     */
    getToken(): string | null {
        return this.token;
    }

    /**
     * Get Axios instance
     */
    get axios(): AxiosInstance {
        return this.client;
    }

    /**
     * Set token from OAuth callback (e.g., from URL parameter)
     */
    async setTokenFromOAuth(token: string): Promise<void> {
        // Ensure initialization is done so we don't get overwritten
        await this.initPromise;
        await this.saveToken(token);
    }
}

export const coreApiClient = new ApiClient();
